import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";

const when = (iso) => (iso ? fmtDateTime(iso) : "—");
const STATUS_CLS = { ok: "text-brand", partial: "text-amber-700", error: "text-rose-600", running: "text-sky-700", skipped: "text-slate-500" };

const RunRow = ({ run }) => {
  const c = run.counts || {};
  const p = run.photos || {};
  return (
    <tr>
      <td className="whitespace-nowrap py-2 pr-3">{when(run.started_at)}</td>
      <td className="py-2 pr-3">{run.mode} · {run.trigger}</td>
      <td className={`py-2 pr-3 font-semibold ${STATUS_CLS[run.status] || ""}`}>{run.status}</td>
      <td className="whitespace-nowrap py-2 pr-3 tabular-nums">{c.items_changed ?? 0}/{c.items_removed ?? 0} · {c.categories_changed ?? 0} · {c.prices_changed ?? 0}/{c.prices_removed ?? 0}</td>
      <td className="whitespace-nowrap py-2 pr-3 tabular-nums">{p.processed ?? 0}/{p.missing ?? 0}/{p.failed ?? 0}</td>
      <td className="py-2 text-rose-600">{(run.errors || []).slice(0, 3).join(" · ")}</td>
    </tr>
  );
};

export const SyncPanel = ({ sync, onChanged }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const s = sync.state || {};

  const run = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalogue-sync/run", { mode });
      data.started ? toast.success(t("sync.started")) : toast.info(t("sync.alreadyRunning"));
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setBusy(false);
      setTimeout(onChanged, 1500);
    }
  };

  return (
    <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm" data-testid="sync-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-navy md:text-lg">{t("sync.title")}</h2>
          <p className="text-xs text-slate-500">{t("sync.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy || s.running} onClick={() => run("incremental")} data-testid="sync-run-now" className="flex h-11 items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-[#25a065] disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${s.running ? "animate-spin" : ""}`} /> {t("sync.runNow")}
        </button>
        <button type="button" disabled={busy || s.running} onClick={() => run("full")} data-testid="sync-run-full" className="h-11 rounded-full border border-navy/15 px-5 text-sm font-semibold text-navy transition-colors hover:bg-canvas disabled:opacity-50">
          {t("sync.runFull")}
        </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs lg:grid-cols-5" data-testid="sync-state">
        <div className="rounded-xl bg-canvas p-3"><div className="text-slate-500">{t("sync.status")}</div><div className={`mt-1 text-sm font-semibold ${STATUS_CLS[s.running ? "running" : s.last_status] || "text-navy"}`} data-testid="sync-status-value">{s.running ? t("sync.running") : s.last_status || "—"}</div></div>
        <div className="rounded-xl bg-canvas p-3"><div className="text-slate-500">{t("sync.lastRun")}</div><div className="mt-1 text-sm font-semibold text-navy" data-testid="sync-last-run">{when(s.last_run_at)}</div></div>
        <div className="rounded-xl bg-canvas p-3"><div className="text-slate-500">{t("sync.lastFull")}</div><div className="mt-1 text-sm font-semibold text-navy">{when(s.last_full_at)}</div></div>
        <div className="rounded-xl bg-canvas p-3"><div className="text-slate-500">{t("sync.cached")}</div><div className="mt-1 text-sm font-semibold text-navy" data-testid="sync-cached">{sync.cached.items} / {sync.cached.categories} / {sync.cached.prices}</div></div>
        <div className="rounded-xl bg-canvas p-3"><div className="text-slate-500">{t("sync.photoStats")}</div><div className="mt-1 text-sm font-semibold text-navy" data-testid="sync-photo-stats">{sync.photo_stats.ok} / {sync.photo_stats.missing} / {sync.photo_stats.error}</div></div>
      </div>
      <div className="-mx-5 overflow-x-auto px-5">
        <table className="w-full min-w-[720px] text-xs" data-testid="sync-runs-table">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-2 pr-3 font-medium">{t("statement.date")}</th>
              <th className="py-2 pr-3 font-medium">{t("sync.run")}</th>
              <th className="py-2 pr-3 font-medium">{t("sync.status")}</th>
              <th className="py-2 pr-3 font-medium">{t("sync.changedCols")}</th>
              <th className="py-2 pr-3 font-medium">{t("sync.photoCols")}</th>
              <th className="py-2 font-medium">{t("sync.errors")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-navy">{sync.runs.map((r) => <RunRow key={r.run_id + r.status} run={r} />)}</tbody>
        </table>
      </div>
    </section>
  );
};
